import {
  type NavigationGuardNext,
  type RouteLocationNormalized,
  type RouteRecordRaw,
  createRouter,
  createWebHistory,
} from 'vue-router';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import useInitPanelSession from 'gpcore-apikit/store/pinia/features/useInitPanelSession';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import BillDetails from '@panel/views/user/settings/BillDetails.vue';
import * as Sentry from '@sentry/vue';
import Unauthorized from '../views/Unauthorized.vue';
import Home from '../views/Home.vue';
import ProjectSettings from '../views/project/Settings.vue';
import UserSettings from '../views/user/settings/Settings.vue';
import PublicImages from '../views/resources/PublicImages.vue';
import Log from '../views/project/Log.vue';
import Members from '../views/project/Members.vue';
import CreateNode from '../views/node/Create.vue';
import NodeOverview from '../views/node/Overview.vue';
import NodeAccess from '../views/node/Access.vue';
import NodePower from '../views/node/Power.vue';
import BillingProfiles from '../views/user/settings/BillingProfiles.vue';
import Debug from '../views/Debug.vue';
import SSHKeys from '../views/user/settings/SSHKeys.vue';
import Clients from '../views/user/settings/Clients.vue';
import ProjectNodes from '../views/project/Nodes.vue';
import ProjectList from '../views/project/List.vue';
import CreateProject from '../views/project/Create.vue';
import RouterViewWrapper from '../views/wrapper/RouterViewWrapper.vue';
import NodeReinstall from '../views/project/ReinstallNode.vue';
import ProjectImages from '../views/project/Images.vue';
import CloudProviders from '../views/project/CloudProviders.vue';
import CloudProviderImport from '../views/project/CloudProviderImport.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import Welcome from '@panel/views/Welcome.vue';

const projectViewGuard = (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  isRoot: boolean = false,
) => {
  const projectStore = useProjectStore();

  // in all cases, if the project list is empty, we need to go to project creation
  if (projectStore.projects.length === 0) {
    next({ name: 'ProjectCreate' });
    return;
  }

  // If we have a project ID from URL but the current pinia project ID differs,
  if (to.params.projectId != null) {
    // fix the project ID
    if (projectStore.project == null || projectStore.projectId == null) {
      projectStore.changeProject(projectStore.defaultProjectId);
    }

    if (to.params.projectId === projectStore.projectId) {
      if (isRoot && to.path.endsWith(to.params.projectId)) {
        next({ name: 'ProjectNodes', params: { projectId: projectStore.projectId } });
        return;
      }

      next();
      return;
    }

    // we need to check if the project is still available
    const findProject = projectStore.projects.find((p) => p.id === to.params.projectId);

    if (findProject == null) {
      // not found -> change to default project instead
      projectStore.changeProject(projectStore.defaultProjectId);
    } else {
      // change to the project to make sure we have the correct project data
      projectStore.changeProject(to.params.projectId as string);
    }

    next({ name: 'ProjectNodes', params: { projectId: projectStore.projectId } });
    return;
  }

  next();
  return;
};

const publicRoutes: Array<RouteRecordRaw> = [
  {
    path: '/unauthorized',
    name: 'Unauthorized',
    component: Unauthorized,
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      const sessionStore = useSessionStore();
      // Only redirect back if we have a valid origin that isn't the root route,
      // otherwise we'd loop: / → Unauthorized → / → ...
      if (sessionStore.isLoggedIn && from.name != null && from.name !== 'Unauthorized') {
        next(from);
        return;
      }

      next();
    },
  },
];

const sessionRoutes: Array<RouteRecordRaw> = [
  {
    path: '/',
    component: RouterViewWrapper,
    beforeEnter: async (
      to: RouteLocationNormalized,
      from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      const sessionStore = useSessionStore();
      const { addNotification } = useNotifications();

      // Check if user is already logged in
      if (!sessionStore.isLoggedIn) {
        // clean up storage
        localStorage.clear();
        next({ name: 'Unauthorized' });

        return;
      }

      try {
        const impersonationToken = to.query['impersonate-token'] as string;
        const impersonateTokenExpiresAt = to.query['impersonate-token-expires-at'] as string;
        await useInitPanelSession({
          token: impersonationToken,
          expiresAt: impersonateTokenExpiresAt,
          onRefreshTokenError: () => {
            addNotification({
              type: 'danger',
              message: 'Failed to refresh token, reloading in 5 seconds.',
            });
          },
        });
      } catch (e) {
        // if the init session fails, we need to log out
        Sentry.captureException(e);
        next({ name: 'Unauthorized' });
        return;
      }

      next();
    },
    children: [
      // Home redirect page
      {
        path: '/',
        name: 'Home',
        component: Home,
        beforeEnter: (from, to, next) => {
          const projectStore = useProjectStore();
          if (projectStore.projects.length === 0) {
            projectStore.changeProject(null);

            // go to project creation, if no projects are available
            next({ name: 'ProjectCreate' });
            return;
          }

          if (projectStore.projectId == null) {
            projectStore.changeProject(projectStore.defaultProjectId);
          } else {
            const findProject = projectStore.projects.find((p) => p.id === projectStore.projectId);
            if (findProject == null) {
              // not found -> change to default project instead
              projectStore.changeProject(projectStore.defaultProjectId);
            }

            // otherwise, no need to change project
          }

          // go straight to the project nodes
          next({ name: 'ProjectNodes', params: { projectId: projectStore.projectId } });
        },
      },
      {
        path: '/welcome',
        name: 'Welcome',
        component: Welcome,
      },
      {
        path: '/debug',
        name: 'Debug',
        component: Debug,
        beforeEnter: async (
          to: RouteLocationNormalized,
          from: RouteLocationNormalized,
          next: NavigationGuardNext,
        ) => {
          const sessionStore = useSessionStore();
          // only allow super-admins to access debug page, otherwise redirect to home
          if (!sessionStore.hasRole('super-admin')) {
            next({ name: 'Home' });
            return;
          }
          next();
        },
      },
      // Public resources for all users/projects
      { path: '/resources/images', name: 'PublicImages', component: PublicImages },
      // User
      { path: '/user/settings', name: 'UserSettings', component: UserSettings },
      {
        path: '/user/settings/billing-profiles',
        name: 'UserBillingProfiles',
        component: BillingProfiles,
      },
      { path: '/user/settings/ssh', name: 'UserSSHKeys', component: SSHKeys },
      { path: '/user/settings/clients', name: 'UserClients', component: Clients },
      {
        path: '/user/settings/billing-profiles/:billingProfileId/:billId',
        name: 'BillDetail',
        component: BillDetails,
      },
      { path: '/projects', name: 'ProjectList', component: ProjectList },
      // Project creation
      { path: '/project/create', name: 'ProjectCreate', component: CreateProject },
      // Project routes with projectId parameter
      {
        path: '/project/:projectId',
        name: 'ProjectWrapper',
        component: RouterViewWrapper,
        beforeEnter: [
          (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) =>
            projectViewGuard(to, from, next, true),
        ],
        children: [
          {
            path: '/project/:projectId/settings',
            name: 'ProjectSettings',
            component: ProjectSettings,
            beforeEnter: [projectViewGuard],
          },
          {
            path: '/project/:projectId/log',
            name: 'ProjectAuditLog',
            component: Log,
            beforeEnter: [projectViewGuard],
          },
          {
            path: '/project/:projectId/members',
            name: 'ProjectMembers',
            component: Members,
            beforeEnter: [projectViewGuard],
          },
          {
            path: '/project/:projectId/cloud-providers',
            name: 'ProjectCloudProviders',
            component: CloudProviders,
            beforeEnter: [projectViewGuard],
          },
          {
            path: '/project/:projectId/cloud-providers/:providerId:/import',
            name: 'ProjectCloudProviderImport',
            component: CloudProviderImport,
            beforeEnter: [projectViewGuard],
          },
          {
            path: '/project/:projectId/nodes/create',
            name: 'ProjectNodeCreate',
            component: CreateNode,
            beforeEnter: [projectViewGuard],
          },
          {
            path: '/project/:projectId/nodes',
            name: 'ProjectNodes',
            component: ProjectNodes,
            beforeEnter: [projectViewGuard],
          },
          {
            path: '/project/:projectId/nodes/:id/overview',
            name: 'ProjectNodeOverview',
            component: NodeOverview,
          },
          {
            path: '/project/:projectId/nodes/:id/access',
            name: 'ProjectNodeAccess',
            component: NodeAccess,
          },
          {
            path: '/project/:projectId/nodes/:id/power',
            name: 'ProjectNodePower',
            component: NodePower,
          },
          {
            path: '/project/:projectId/nodes/:id/reinstall',
            name: 'ProjectNodeReinstall',
            component: NodeReinstall,
          },
          {
            path: '/project/:projectId/images',
            name: 'ProjectImages',
            component: ProjectImages,
          },
        ],
      },
    ],
  },
];

const routes: Array<RouteRecordRaw> = [...publicRoutes, ...sessionRoutes];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

export default router;
